/** The sheet purpose a system's *dead at* is declared under (qa-runs 394): the same data the sheet's status words read. */
export const DEAD_AT_PURPOSE = 'hp_dead_at';
/** The farthest a threshold may be from 0. */
export const DEAD_AT_LIMIT = 1000;
export const isDeadAt = (v) => typeof v === 'number' && Number.isInteger(v) && Math.abs(v) <= DEAD_AT_LIMIT;
const isWhole = (v, least, most) => typeof v === 'number' && Number.isInteger(v) && v >= least && v <= most;
const isNameOrNull = (v) => v === null || (typeof v === 'string' && v.length >= 1 && v.length <= 40);
const DEATH_WORDS = ['success', 'failure', 'regain_1', 'two_failures'];
export function parseDying(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const d = v;
    if (!isWhole(d.successes, 0, 20) || !isWhole(d.failures, 0, 20) || (d.stable !== undefined && d.stable !== true) || (d.saved !== undefined && !isWhole(d.saved, 0, 1_000_000)))
        return null;
    return { successes: d.successes, failures: d.failures, ...(d.stable ? { stable: true } : {}), ...(d.saved !== undefined ? { saved: d.saved } : {}) };
}
export function parseDyingStamp(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const d = v;
    if (!isDeadAt(d.floor) || !(d.massive === null || isWhole(d.massive, 0, 1_000_000)) || !isNameOrNull(d.onZero) || !isNameOrNull(d.stable) || !isWhole(d.failures, 1, 20) || !isWhole(d.fails, 0, 20))
        return null;
    return { floor: d.floor, massive: d.massive, onZero: d.onZero, stable: d.stable, failures: d.failures, fails: d.fails };
}
export function parseDeathThrow(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const d = v;
    if (!isWhole(d.face, 1, 1000) || !DEATH_WORDS.includes(d.word) || !isDeadAt(d.floor) || !isWhole(d.successes, 1, 20) || !isWhole(d.failures, 1, 20) || !isNameOrNull(d.onZero) || !isNameOrNull(d.stable) || !isWhole(d.round, 0, 1_000_000))
        return null;
    return { face: d.face, word: d.word, floor: d.floor, successes: d.successes, failures: d.failures, onZero: d.onZero, stable: d.stable, round: d.round };
}
const named = (list, name) => !!name && (list ?? []).some((c) => c.trim().toLowerCase() === name.toLowerCase());
const withName = (list, name) => (!name || named(list, name) ? (list ? [...list] : undefined) : [...(list ?? []), name].slice(0, 20));
const withoutNames = (list, names) => {
    const out = list?.filter((c) => !names.some((n) => n && c.trim().toLowerCase() === n.toLowerCase()));
    return out?.length ? out : undefined;
};
/** Marks with their keys as a token holds them: gone when there are none. */
function settled(v, m) {
    const { dying: _d, conditions: _c, ...rest } = v;
    return { ...rest, hp: m.hp, ...(m.dying ? { dying: m.dying } : {}), ...(m.conditions ? { conditions: m.conditions } : {}) };
}
/**
 * A character's hit points after a change, under its system's dying: they stop at the floor, and what goes past it is
 * held against `massive`. Falling to it puts on `onZero` and starts the marks; damage while down adds failures and
 * undoes a stable; anything above the floor clears the marks and those conditions. Pure.
 */
export function afterHurt(before, hp, s) {
    const was = before.hp?.current ?? hp.current;
    const floored = { ...hp, current: Math.max(s.floor, hp.current) };
    if (floored.current > s.floor)
        return { values: settled(before, { hp: floored, dying: undefined, conditions: withoutNames(before.conditions, [s.onZero, s.stable]) }), dead: false };
    const past = s.floor - hp.current;
    const massive = s.massive !== null && past > 0 && past >= s.massive;
    if (was > s.floor)
        return { values: settled(before, { hp: floored, dying: { successes: 0, failures: 0 }, conditions: withName(before.conditions, s.onZero) }), dead: massive };
    if (hp.current >= was)
        return { values: settled(before, { hp: floored, dying: before.dying, conditions: before.conditions ? [...before.conditions] : undefined }), dead: false };
    const failures = (before.dying?.failures ?? 0) + s.fails;
    const { stable: _stable, ...marks } = before.dying ?? { successes: 0, failures: 0 };
    return { values: settled(before, { hp: floored, dying: { ...marks, failures }, conditions: withoutNames(before.conditions, [s.stable]) }), dead: massive || failures >= s.failures };
}
/** What a face of a death save says: what `natural` names for it, else against the target. */
export const deathWord = (s, face) => s.natural.find((n) => n.face === face)?.result ?? (face >= s.target ? 'success' : 'failure');
/** A token after its death save. Pure. */
export function afterDeathSave(before, t) {
    const hp = before.hp ?? { current: t.floor, max: 1 };
    if (t.word === 'regain_1')
        return { values: settled(before, { hp: { ...hp, current: t.floor + 1 }, dying: undefined, conditions: withoutNames(before.conditions, [t.onZero, t.stable]) }), dead: false };
    const marks = before.dying ?? { successes: 0, failures: 0 };
    if (t.word === 'success') {
        const successes = marks.successes + 1;
        const stable = successes >= t.successes;
        return { values: settled(before, { hp, dying: { ...marks, successes, saved: t.round, ...(stable ? { stable: true } : {}) }, conditions: stable ? withName(before.conditions, t.stable) : before.conditions ? [...before.conditions] : undefined }), dead: false };
    }
    const failures = marks.failures + (t.word === 'two_failures' ? 2 : 1);
    return { values: settled(before, { hp, dying: { ...marks, failures, saved: t.round }, conditions: before.conditions ? [...before.conditions] : undefined }), dead: failures >= t.failures };
}
/** The threshold a linked character's sheet declares (`hp_dead_at`), or null when it carries none. */
export function sheetDeadAt(sheet) {
    const n = sheet?.find((s) => s.purpose === DEAD_AT_PURPOSE);
    return n && isDeadAt(n.value) ? n.value : null;
}
/** The threshold a stat block gives under the system's rule, or null: no rule, or a Constitution the rule needs is unknown. */
export function statDeadAt(rule, con) {
    if (!rule)
        return null;
    if (rule.less === 'con')
        return typeof con === 'number' && Number.isInteger(con) ? Math.max(-DEAD_AT_LIMIT, rule.at - con) : null;
    return rule.at;
}
/**
 * A token's threshold: none without hit points; 0 for a monster (not a player character) where the campaign's
 * *Monsters die at 0 hit points* is on; else its sheet's, else its stat block's; null, it dies only by the DM's mark.
 */
export function deadAtOf(token, o) {
    if (!token.values.hp)
        return null;
    if (o.monstersAtZero && !token.pc)
        return 0;
    return sheetDeadAt(token.values.sheet) ?? (isDeadAt(o.statBlock) ? o.statBlock : null);
}
/** Whether a change of hit points kills: they went down, to the stamped threshold or below. */
export function diesAt(before, after, deadAt) {
    return deadAt !== undefined && !!after && after.current <= deadAt && (!before || after.current < before.current);
}
/**
 * The combatants who are dead (qa-runs 401): each with a token on the table and every one of them marked dead. A
 * combatant with no token is never dead. Stamped by the server on an End turn, so the turn passes over them.
 */
export function deadCombatants(combatants, objects) {
    return combatants.filter((c) => {
        const on = c.tokens.map((id) => objects[id]).filter((o) => !!o);
        return on.length > 0 && on.every((o) => o.dead);
    }).map((c) => c.id);
}
//# sourceMappingURL=death.js.map